/**
 * System Summary: what the model says this system IS, and what it is allowed.
 *
 * Renamed from Budgets on 2026-09-01 - briefly System Attributes, which is now
 * the name of its FIRST SECTION rather than of the page, because the page is
 * four answers and only one of them is the system's own attributes. The rename
 * is the smaller half of the change. A budget was one question - what does the subtree add up to - and the
 * page answered it with tiles and bars. There are now two, and they are
 * different shapes with different audiences:
 *
 *   1. **The system's own properties.** Centre of gravity, inertia tensor,
 *      envelope volume, delta-V, pointing, slew, jitter. Stated on the system,
 *      derived from nothing, and several of them deliberately do NOT roll up -
 *      delta-V is a function of the mass ratio, allocated downward, which is the
 *      opposite direction from a sum. Some are not scalars: an inertia tensor is
 *      six terms about a point that is three more.
 *   2. **The rollups and their budgets.** The old page, plus the comparison the
 *      server now performs: `budgets` on every row, five-valued.
 *
 * ONE PAGE, TWO SECTIONS - Patrick, 2026-09-01, over two routes. They are one
 * fetch and one root picker, and splitting them would let somebody read half the
 * numbers and never learn the other half existed.
 *
 * REVERSED 2026-09-01. This file used to say "there is deliberately NO
 * comparison against a target and no budget-exceeded check, not even disabled".
 * The server compares, and this screen renders it.
 *
 * THE RULE THIS SCREEN EXISTS TO NOT BREAK:
 *
 *   A partial total can prove an overrun and can never prove a margin.
 *
 * `status` is five-valued for that reason and `margin` is null on
 * `indeterminate` by construction. A vehicle allowed 200 kg reading 141.85 with
 * the flight software unstated does not have 58 kg of headroom - the parts that
 * state nothing could take it over. So: nothing here computes a margin, nothing
 * here compares a total to a budget, and `indeterminate` is never green. All
 * three are enforced in `budget-model.ts` and tested directly, because on this
 * screen a wrong answer is indistinguishable from a right one.
 *
 * The other direction is the same mistake mirrored. `bound` says which way a
 * number is wrong: `at_least` renders `≥`, `at_most` renders `≤`. A `min`
 * aggregation - radiation tolerance, the weakest part in an assembly - is too
 * HIGH when something below it is unstated, so its figure is a ceiling.
 * Rendering that as a floor turns a shortfall into a margin.
 *
 * WHICH quantities appear is `attributes` from the payload and WHICH properties
 * appear is the registry's own field list, grouped by feature path. No list of
 * attributes lives in this file: a quantity added to the library gets a tile, a
 * chart, a column and a card with nothing on this side being edited, which is
 * the entire reason the markers live in the model.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../api'
import EmptyState from '../components/common/EmptyState'
import HelpTip from '../components/common/HelpTip'
import AllocationTable from '../components/budgets/AllocationTable'
import BreakdownChart from '../components/budgets/BreakdownChart'
import BudgetTable from '../components/budgets/BudgetTable'
import GroupCard from '../components/budgets/SystemProperties'
import QuantityTile from '../components/budgets/QuantityTile'
import {
  allocationRows,
  breakdowns as breakdownsOf,
  budgetOf,
  countVerdicts,
  defaultRoot,
  designRoots,
  readBudget,
  subtreeOf,
} from '../components/budgets/budget-model'
import type { DesignBudget } from '../components/budgets/budget-model'
import { attributeGroups, statedGroups } from '../components/budgets/system-attributes'
import StatePicker, {
  machineOptions,
  scopeOf,
  stateOptions,
} from '../components/budgets/StatePicker'
import type { EntityRow, PartDetail } from '../schema'
import { MONOSPACE } from '../theme'
import { findKind } from '../schema'
import { useSchema } from '../state/AppData'

/*
 * The explanations, kept and moved behind a `?`.
 *
 * None of these is decoration - roots that overlap and must never be summed is
 * the fact that stops somebody adding up six numbers and believing the answer -
 * but none of them is needed on the third visit either, and printed in full they
 * pushed the figures they describe below the fold. The screen states what is
 * true; the tip explains why. See `HelpTip`.
 */
export const ROOTS_NOTE =
  'Every part nothing contains is a top-level part, so a definition used inside the ' +
  'vehicle and also declared on its own appears twice over. They overlap, which is ' +
  'why one is reported at a time and they are never added together.'

export const SOURCE_NOTE =
  'Every rolled-up figure comes from the server: a part’s own stated value plus the ' +
  'total of each part inside it, multiplied by its multiplicity. Which quantities ' +
  'roll up, and which are budgeted, comes from the model rather than from this screen.'

export const PROPERTIES_NOTE =
  'Stated on the system itself rather than rolled up from the parts inside it. ' +
  'Several deliberately do not aggregate — components pack and leave voids, so their ' +
  'volumes do not sum, and delta-V follows from the mass ratio and is allocated ' +
  'downward rather than upward.'

type View = 'charts' | 'table'

/**
 * One section heading.
 *
 * Bolder and larger than body text, because these divide the page into four
 * answers to four different questions and an `overline` in secondary grey read
 * as a caption on the block above it rather than as the start of the block
 * below. The rule stays, so the division is visible at a glance as well as
 * legible.
 */
function Section({ title, help, helpLabel }: {
  title: string
  /** The paragraph that used to sit under the heading. */
  help?: string
  helpLabel?: string
}) {
  return (
    <Divider textAlign="left" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          {title}
        </Typography>
        {help && <HelpTip title={help} label={helpLabel ?? `About ${title}`} />}
      </Box>
    </Divider>
  )
}

export default function SystemSummary() {
  const schema = useSchema()
  const navigate = useNavigate()
  const kind = findKind(schema, 'parts')

  const [budget, setBudget] = useState<DesignBudget | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [reloads, setReloads] = useState(0)
  const [chosen, setChosen] = useState('')
  // Which state the page resolves in. '' is the BASELINE and is the default:
  // opening on some arbitrary state would answer a question nobody asked.
  const [state, setState] = useState('')
  const [states, setStates] = useState<EntityRow[]>([])
  const [view, setView] = useState<View>('charts')

  /**
   * The chosen root's DEFINITION, which is where its own properties live.
   *
   * A second request, and it has to be: `/api/design` answers totals and
   * budgets, not stated values, and the design tree is almost entirely usages -
   * `part vehicle : Vehicle` states nothing at all, because the values are on
   * `Vehicle`. A screen that read the usage would show a fully specified
   * vehicle as blank.
   */
  const [properties, setProperties] = useState<PartDetail | null>(null)

  useEffect(() => {
    let live = true
    setError(null)
    api
      .designBudget(scopeOf(state))
      .then((answer) => live && setBudget(answer))
      .catch((failure: ApiError) => live && setError(failure))
    return () => {
      live = false
    }
  }, [reloads, state])

  // The states worth offering, fetched once. Separate from the budget request
  // because it does not change when the chosen state does - re-fetching the
  // list every time somebody picks from it would be asking the server to
  // repeat itself.
  useEffect(() => {
    let live = true
    api
      .list('states', { limit: 5000 })
      .then((page) => live && setStates(page.items))
      // A program with no states is the ordinary case, not an error: the
      // picker simply does not render. Failing the whole page over it would
      // take the budgets down with it.
      .catch(() => live && setStates([]))
    return () => {
      live = false
    }
  }, [reloads])

  const stateChoices = useMemo(() => stateOptions(states), [states])
  const machineChoices = useMemo(() => machineOptions(states), [states])

  const roots = useMemo(() => designRoots(budget?.items ?? []), [budget])

  // The chosen root is held as an INDEX into the flat rows, not as an ID: a
  // definition used twice appears twice in the tree, so an ID does not identify a
  // position and picking by one would silently show the first occurrence.
  const fallback = useMemo(() => defaultRoot(budget?.items ?? []), [budget])
  const at = chosen === '' ? fallback?.index : Number(chosen)
  const root = at === undefined ? null : (budget?.items[at] ?? null)

  // Follow the usage to its definition, then read the definition's values. Two
  // hops in the worst case and one where the root already IS a definition; the
  // alternative is a `definition` column on every design row for the benefit of
  // this one panel.
  const rootId = root?.id
  useEffect(() => {
    if (!rootId) {
      setProperties(null)
      return undefined
    }
    let live = true
    setProperties(null)
    api
      .part(rootId)
      .then((answer) => {
        if (!live) return null
        if (answer.is_definition || !answer.definition) {
          setProperties(answer)
          return null
        }
        return api.part(answer.definition).then((defined) => {
          if (live) setProperties(defined)
          return null
        })
      })
      // A property panel that cannot load must not take the budgets down with
      // it: the two are independent requests answering independent questions.
      .catch(() => live && setProperties(null))
    return () => {
      live = false
    }
  }, [rootId])

  const breakdowns = useMemo(
    () => (budget && at !== undefined ? breakdownsOf(budget, at) : []),
    [budget, at],
  )

  const allocations = useMemo(
    () =>
      budget && at !== undefined ? allocationRows(budget.items, at, budget.attributes) : [],
    [budget, at],
  )

  const verdicts = useMemo(() => {
    if (!budget || at === undefined) return null
    const rows = subtreeOf(budget.items, at).map((entry) => entry.row)
    return countVerdicts(rows, budget.attributes)
  }, [budget, at])

  // The rolling quantities have tiles and charts of their own, so they are left
  // out of the properties: the same number twice in two shapes reads as two
  // different numbers.
  const groups = useMemo(
    () =>
      attributeGroups(
        kind,
        properties as Record<string, unknown> | null,
        (budget?.attributes ?? []).map((attribute) => attribute.name),
      ),
    [kind, properties, budget],
  )
  const { stated, unstated } = useMemo(() => statedGroups(groups), [groups])

  const problems = useMemo(
    () => Array.from(new Set(breakdowns.flatMap((breakdown) => breakdown.problems))),
    [breakdowns],
  )

  if (!kind) {
    return (
      <EmptyState
        title="This BEST does not model parts"
        description="There is no part kind in the registry, so there is no decomposition to report on."
        actions={[{ label: 'Go to the dashboard', onClick: () => navigate('/') }]}
      />
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load the system attributes"
        description={error.message}
        actions={[{ label: 'Try again', onClick: () => setReloads((n) => n + 1) }]}
      />
    )
  }

  if (!budget) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="h5">System Summary</Typography>
      <HelpTip title={SOURCE_NOTE} label="Where these figures come from" />
    </Box>
  )

  // An unstorable quantity is reported whatever else the page can show: it is a
  // statement about the MODEL, not about the part in hand.
  const unstorable = budget.unstorable.length > 0 && (
    <Alert severity="info">
      <AlertTitle>
        {budget.unstorable.length} quantit{budget.unstorable.length === 1 ? 'y rolls' : 'ies roll'}{' '}
        up in the model but cannot be stored
      </AlertTitle>
      <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
        {budget.unstorable.join(', ')}
      </Box>
      BEST has no column behind{' '}
      {budget.unstorable.length === 1 ? 'this attribute' : 'these attributes'}, so there is
      nothing to sum and no figure below. Named rather than left out: the model says the
      quantity aggregates, and a screen that never mentions it reads as a screen that
      forgot.
    </Alert>
  )

  if (!root || budget.attributes.length === 0) {
    return (
      <Stack spacing={2}>
        {header}
        {unstorable}
        <EmptyState
          title={
            budget.items.length === 0
              ? 'There is no design to report on'
              : 'This program declares no quantity that rolls up'
          }
          description={
            budget.items.length === 0
              ? 'A rollup runs over containment, so it needs a part usage. A part ' +
                'definition on its own is a declaration rather than a decomposition.'
              : 'An attribute appears here when it is marked @EchelonSysML::RollsUp in the ' +
                'library. Nothing in this model is, so there is nothing to total.'
          }
          actions={[
            { label: `Go to ${kind.plural}`, onClick: () => navigate(`/${kind.plural}`) },
          ]}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      {header}
      {unstorable}

      {stateChoices.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <StatePicker
            options={stateChoices}
            machines={machineChoices}
            value={state}
            onChange={setState}
          />
        </Paper>
      )}

      {roots.length > 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
            <TextField
              select
              size="small"
              label="Top-level part"
              value={String(at)}
              onChange={(event) => setChosen(event.target.value)}
              sx={{ minWidth: 280 }}
            >
              {roots.map((entry) => (
                <MenuItem key={entry.index} value={String(entry.index)}>
                  {entry.row.name}
                  {/* What the MODEL says it is, not what the row count guesses.
                      A part specializing EchelonSystem says it is the thing
                      system figures are reported for. */}
                  {entry.row.is_system ? ' — the system' : ''}
                  {entry.descendants > 0
                    ? ` — ${entry.descendants} part${entry.descendants === 1 ? '' : 's'} inside`
                    : ' — nothing inside'}
                </MenuItem>
              ))}
            </TextField>
            <HelpTip title={ROOTS_NOTE} label="Why there is more than one top-level part" />
          </Stack>
        </Paper>
      )}

      {/* ------------------------------------------------------------------
          SECTION ONE - what the system IS.
          ------------------------------------------------------------------ */}
      <Box>
        <Section
          title="System Attributes"
          help={PROPERTIES_NOTE}
          helpLabel="What a system attribute is"
        />

        {stated.length === 0 ? (
          // A BORDER RATHER THAN A SENTENCE. This read as a paragraph of grey
          // text where a panel should be, and a section that states nothing
          // looked like a section that failed to load - BALROG's was taken for
          // exactly that. An outlined empty slot says "this belongs here and is
          // empty", which is the true statement.
          <Paper
            variant="outlined"
            sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}
          >
            {/* THE SHORT TRUE SENTENCE, with the reasoning one hover away.
                'None set' is the whole answer; why nothing can be computed in
                their absence is worth saying once and not on every visit. */}
            <Typography variant="body2" color="text.secondary">
              {properties ? 'No system attributes set' : 'Loading this part’s stated properties…'}
              {properties && (
                <HelpTip
                  title={
                    `${root.name} states none of the ${groups.length} properties the profile ` +
                    'declares. They are not derived from anything below, so there is nothing to ' +
                    'compute in their absence — each is a value somebody has to write down.'
                  }
                  label="Why there is nothing here"
                />
              )}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {stated.map((group) => (
              <Grid
                key={group.feature}
                // A tensor needs the room; a single figure does not.
                size={{ xs: 12, sm: group.isStructured ? 12 : 6, md: group.isStructured ? 6 : 3 }}
              >
                <GroupCard group={group} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* COUNTED, not dropped. Silence here reads as a screen that only knows
            about the five that are filled in. */}
        {stated.length > 0 && unstated.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {unstated.length} further propert{unstated.length === 1 ? 'y states' : 'ies state'}{' '}
            nothing: {unstated.map((group) => group.label).join(', ')}.
          </Typography>
        )}
      </Box>

      {/* ------------------------------------------------------------------
          SECTION TWO - what the subtree adds up to, and what it is allowed.
          ------------------------------------------------------------------ */}
      <Box>
        <Section title="Rollup Attributes" />

        {verdicts && (verdicts.over > 0 || verdicts.indeterminate > 0) && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            {verdicts.over > 0 && (
              <Chip color="error" label={`${verdicts.over} over budget`} size="small" />
            )}
            {verdicts.indeterminate > 0 && (
              <Chip
                color="warning"
                size="small"
                label={`${verdicts.indeterminate} not settled`}
              />
            )}
            {verdicts.within > 0 && (
              <Chip color="success" label={`${verdicts.within} within`} size="small" />
            )}
            {verdicts.not_comparable > 0 && (
              <Chip
                variant="outlined"
                size="small"
                label={`${verdicts.not_comparable} not compared`}
              />
            )}
          </Stack>
        )}

        <Grid container spacing={2}>
          {budget.attributes.map((attribute) => (
            <Grid key={attribute.name} size={{ xs: 12, sm: 6, md: 4 }}>
              <QuantityTile
                attribute={attribute}
                total={root.totals?.[attribute.name] ?? null}
                budget={readBudget(budgetOf(root, attribute.name))}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {problems.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>
            {problems.length} value{problems.length === 1 ? '' : 's'} could not be summed
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </Box>
          Each is reported rather than counted as zero, and the subtree it sits in stopped
          summing there. A unit that disagrees is never converted, and a value nobody can
          parse is never guessed at.
        </Alert>
      )}

      {/* ------------------------------------------------------------------
          The budgets themselves - every allocation in this root's subtree.
          ------------------------------------------------------------------ */}
      <Box>
        <Section title="Budgets" />
        <Paper sx={{ p: 1.5 }}>
          <AllocationTable rows={allocations} rootName={root.name} />
        </Paper>
      </Box>

      <Box>
        <Section title="Breakdown" />
        <Tabs value={view} onChange={(_event, next: View) => setView(next)} sx={{ mb: 2 }}>
          <Tab value="charts" label="Charts" />
          <Tab value="table" label="Table" />
        </Tabs>

        {view === 'charts' ? (
          <Grid container spacing={2}>
            {breakdowns.map((breakdown) => (
              <Grid key={breakdown.attribute.name} size={{ xs: 12, xl: 6 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <BreakdownChart breakdown={breakdown} rootName={root.name} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 1.5 }}>
            <BudgetTable breakdowns={breakdowns} rootName={root.name} />
          </Paper>
        )}
      </Box>
    </Stack>
  )
}
